import {
  FileText,
  LayoutTemplate,
  GitBranch,
  Sparkles,
  ShieldCheck,
  FileCheck2,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { DownloadAppButton } from './DownloadAppButton';
import type { Go } from './types';

const FEATURES = [
  {
    icon: FileText,
    title: 'Master specifications',
    body: 'Maintain your firm’s gold-standard sections once. Reuse them across every project with core-and-variant control.',
  },
  {
    icon: LayoutTemplate,
    title: 'Forma formatting engine',
    body: 'Pixel-accurate Word output — headers, footers, numbering, and tokens resolve automatically at export.',
  },
  {
    icon: GitBranch,
    title: 'Draft → review → issue',
    body: 'A git-like workflow built for A/E teams: draft, review, publish, and track issuances and addenda.',
  },
  {
    icon: Sparkles,
    title: 'One master, every client',
    body: 'Layer a client’s own wording on top of the firm’s master without forking it. The master stays the single source of truth, and every client’s version is one click from a clean Word export.',
  },
  {
    icon: ShieldCheck,
    title: 'Built for firms',
    body: 'Roles, permissions, and per-firm isolation so your specs and standards stay yours.',
  },
  {
    icon: FileCheck2,
    title: 'Project manuals that ship',
    body: 'Compile a full project manual, with intentional blank pages and parity, ready to issue.',
  },
];

const STEPS = [
  {
    n: '1',
    title: 'Build your masters',
    body: 'Import or author your firm’s standard sections as living masters.',
  },
  {
    n: '2',
    title: 'Compile a project',
    body: 'Pull masters into a project spec, then tailor the variants you need.',
  },
  {
    n: '3',
    title: 'Format & issue',
    body: 'Apply a Forma template and export a polished, issue-ready manual.',
  },
];

export function HomePage({ go }: { go: Go }) {
  return (
    <>
      {/* Hero */}
      <section className="border-b border-border bg-bg-subtle">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28 lg:py-32">
          <div className="mx-auto max-w-3xl text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1 text-base font-medium text-muted">
              <Sparkles className="size-4 text-primary" aria-hidden="true" />
              Specifications, reimagined for A/E firms
            </span>
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-text sm:text-5xl lg:text-6xl">
              The specification platform your firm has been waiting for
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted">
              Write masters once, compile project manuals in minutes, and export pixel-perfect Word
              documents — all in one place, on the web or your desktop.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <DownloadAppButton size="lg" className="w-full sm:w-auto" />
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => go('auth')}
              >
                Get started
                <ArrowRight className="size-4" aria-hidden="true" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => go('pricing')}
              >
                See pricing
              </Button>
            </div>
            <p className="mt-4 text-base text-muted">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => go('auth')}
                className="rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-text">
            Everything it takes to run specs
          </h2>
          <p className="mt-4 text-lg text-muted">
            From firm masters to issued manuals — purpose-built for how architects and engineers
            actually work.
          </p>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-xl border border-border bg-surface p-6 transition-colors hover:border-primary"
            >
              <span className="flex size-10 items-center justify-center rounded-lg bg-primary-soft text-primary">
                <Icon className="size-5" aria-hidden="true" />
              </span>
              <h3 className="mt-4 text-xl font-semibold text-text">{title}</h3>
              <p className="mt-2 text-md text-muted">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-border bg-bg-subtle">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-text">
              From master to manual in three steps
            </h2>
          </div>
          <ol className="mt-14 grid gap-8 md:grid-cols-3">
            {STEPS.map((s) => (
              <li key={s.n} className="text-center">
                <span className="mx-auto flex size-11 items-center justify-center rounded-full bg-primary text-lg font-bold text-inverse">
                  {s.n}
                </span>
                <h3 className="mt-4 text-xl font-semibold text-text">{s.title}</h3>
                <p className="mt-2 text-md text-muted">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA band */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="rounded-2xl bg-primary px-6 py-14 text-center text-inverse sm:px-12">
          <h2 className="text-3xl font-bold tracking-tight">Ready to see it on your specs?</h2>
          <p className="mx-auto mt-3 max-w-xl text-lg text-inverse opacity-90">
            Create your firm’s workspace and bring your first sections in today.
          </p>
          <div className="mt-8 flex justify-center">
            <Button size="lg" variant="secondary" onClick={() => go('auth')}>
              Get started
              <ArrowRight className="size-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
